import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import SportsHockeyIcon from "@mui/icons-material/SportsHockey";
import { useNavigate } from "react-router-dom";
import { searchLeagues } from "../../services/api/leagues";
import LeagueCard from "../../components/LeagueCard";
import CityStateAutocomplete from "../../components/CityStateAutocomplete";
import StateSelect from "../../components/StateSelect";
import AuthPromptDialog from "../../components/AuthPromptDialog";
import LoginDialog from "../auth/login-dialog";
import { hasLocallyValidSession } from "../../services/auth/auth-session";
import searchBg from "../../assets/images/net.png";

function useBodyBg(imageUrl, isDark) {
  React.useEffect(() => {
    const { style } = document.body;
    style.backgroundImage = `url(${imageUrl})`;
    style.backgroundSize = "cover";
    style.backgroundPosition = "center";
    style.backgroundAttachment = "fixed";
    if (isDark) {
      style.backgroundColor = "rgba(0, 0, 0, 0.4)";
      style.backgroundBlendMode = "darken";
    }
    return () => {
      style.backgroundImage = "";
      style.backgroundSize = "";
      style.backgroundPosition = "";
      style.backgroundAttachment = "";
      style.backgroundColor = "";
      style.blackgroundBlendMode = "";
    };
  }, [imageUrl, isDark]);
}

// Module-level cache — survives navigation but resets on page refresh
const searchCache = { city: "", state: "", results: [], searched: false, scrollY: 0 };

export default function SearchView() {
  const navigate = useNavigate();
  const theme = useTheme();
  useBodyBg(searchBg, theme.palette.mode === "dark");
  const [city, setCity] = React.useState(searchCache.city);
  const [state, setState] = React.useState(searchCache.state);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [results, setResults] = React.useState(searchCache.results);
  const [searched, setSearched] = React.useState(searchCache.searched);
  const [authPromptLeague, setAuthPromptLeague] = React.useState(null);
  const [loginOpen, setLoginOpen] = React.useState(false);

  // Keep cache in sync with state
  React.useEffect(() => { searchCache.city = city; }, [city]);
  React.useEffect(() => { searchCache.state = state; }, [state]);
  React.useEffect(() => { searchCache.results = results; }, [results]);
  React.useEffect(() => { searchCache.searched = searched; }, [searched]);

  // On mount: re-fetch if there was a prior search, then restore scroll
  React.useEffect(() => {
    if (searchCache.searched && searchCache.city && searchCache.state) {
      performSearch(searchCache.city, searchCache.state).then(() => {
        setTimeout(() => window.scrollTo(0, searchCache.scrollY), 0);
      });
    }
    return () => { searchCache.scrollY = window.scrollY; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const performSearch = async (cityVal, stateVal) => {
    const trimmedCity = cityVal.trim();
    const trimmedState = stateVal.trim();
    if (!trimmedCity || !trimmedState) {
      setError("Please enter a city and state.");
      return;
    }
    setError("");
    setLoading(true);
    setSearched(true);
    try {
      const data = await searchLeagues({ city: trimmedCity, state: trimmedState });
      setResults(Array.isArray(data) ? data : []);
    } catch (err) {
      setResults([]);
      setError(err instanceof Error ? err.message : "Search failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    performSearch(city, state);
  };

  const handleSignUp = (league) => {
    if (!league?.id) return;
    if (hasLocallyValidSession()) {
      navigate(`/leagues/${league.id}/signup`);
    } else {
      setAuthPromptLeague(league);
    }
  };

  const handleAuthPromptLogin = () => {
    setLoginOpen(true);
  };

  const handleLoginSuccess = () => {
    const leagueId = authPromptLeague?.id;
    setAuthPromptLeague(null);
    if (leagueId) {
      navigate(`/leagues/${leagueId}/signup`);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h4" component="h3">
              Find hockey near you
            </Typography>
            <Typography color="text.secondary">
              Search by city and state for leagues and games.
            </Typography>

            <Box component="form" onSubmit={handleSearch}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
                <CityStateAutocomplete
                  cityValue={city}
                  stateValue={state}
                  onCityStateChange={(newCity, newState) => {
                    setCity(newCity);
                    setState(newState);
                  }}
                  onSubmit={performSearch}
                  sx={{ flex: 1, minWidth: 200 }}
                  required
                />
                <StateSelect
                  value={state}
                  onChange={setState}
                  required
                  sx={{ maxWidth: { sm: 180 } }}
                />
                <Button type="submit" variant="contained" disabled={loading} sx={{ width: { xs: "100%", sm: "auto" } }}>
                  {loading ? "Searching…" : "Search"}
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Paper>

        {error ? (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        <LoginDialog
          open={loginOpen}
          onClose={() => setLoginOpen(false)}
          onSuccess={handleLoginSuccess}
        />

        <AuthPromptDialog
          open={Boolean(authPromptLeague)}
          onClose={() => setAuthPromptLeague(null)}
          onConfirm={handleAuthPromptLogin}
          title="Create an account to join"
          body={
            <>
              You&apos;ll need a free Puck-Pickup account to sign up for{" "}
              <strong>{authPromptLeague?.name ?? "this league"}</strong>. It only takes a minute!
            </>
          }
        />

        {searched && !loading && (
          <>
          <Divider sx={{ "&::before, &::after": { borderTop: "2px solid rgba(255, 255, 255, 0.40);" } }}>
            <Chip
              label={
                results.length === 0
                  ? "No leagues found"
                  : `${results.length} ${results.length === 1 ? "league" : "leagues"} found`
              }
              color={results.length === 0 ? "default" : "primary"}
              variant={results.length === 0 ? "outlined" : "filled"}
            />
          </Divider>
          {results.length === 0 && (
            <Paper
              variant="outlined"
              sx={{
                px: 4,
                py: 3.5,
                textAlign: "center",
                borderStyle: "dashed",
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.04)"
                    : "rgba(0,0,0,0.03)",
              }}
            >
              <SportsHockeyIcon sx={{ fontSize: 36, color: "text.disabled", mb: 1 }} />
              <Typography variant="body1" fontWeight={600} gutterBottom>
                No leagues found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No biscuits in the basket today! Try a different city or state.
              </Typography>
            </Paper>
          )}
            <Stack spacing={2}>
              {results.map((league) => (
                <LeagueCard
                  key={league.id}
                  league={league}
                  onSignUp={handleSignUp}
                />
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </Container>
  );
}
